/**
 * useLocaleViewModel — MVVM ViewModel for the locale switcher (issue #140).
 *
 * Responsibilities:
 *   - resolve the currently active locale (from i18next, falling back to
 *     localStorage["intersum.locale"], falling back to the store default)
 *   - load the per-store locales catalog from ConfigService at
 *     `/config/<store>/locales` and expose it to the View
 *   - apply the store default on first render if the user has not yet
 *     picked a locale
 *   - persist the user's pick to localStorage and call i18next.changeLanguage
 *
 * The `?i18n=keys` / `?i18n=missing` debug mode is owned entirely by the
 * keyDisplay i18next post-processor (it reads the URL on each t() call), so
 * the viewmodel deliberately does NOT expose a debug flag.
 *
 * Pattern mirrors the existing useCartViewModel / useCategoriesViewModel:
 * pure hook, React state, no JSX. The View is `LocaleSwitcher.tsx`.
 */

import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { fetchRuntimeConfig, getCachedConfig } from "../services/config";

const LOCALE_STORAGE_KEY = "intersum.locale";
const FALLBACK_LOCALE = "en-US";

export type SwitcherStyle = "pill" | "minimal" | "iconOnly";

export interface LocaleCatalogEntry {
	locale: string;
	currency: string;
}

export interface LocalesCatalog {
	default: string;
	supported: LocaleCatalogEntry[];
	switcherStyle: SwitcherStyle;
}

export interface UseLocaleViewModel {
	currentLocale: string;
	availableLocales: LocaleCatalogEntry[];
	switcherStyle: SwitcherStyle;
	isReady: boolean;
	setLocale: (locale: string) => void;
}

function readStoredLocale(): string | null {
	if (typeof window === "undefined") return null;
	try {
		const stored = window.localStorage.getItem(LOCALE_STORAGE_KEY);
		if (stored && /^[a-z]{2}-[A-Z]{2}$/.test(stored)) return stored;
	} catch {
		// localStorage may throw in private-mode browsers; ignore.
	}
	return null;
}

function writeStoredLocale(locale: string): void {
	if (typeof window === "undefined") return;
	try {
		window.localStorage.setItem(LOCALE_STORAGE_KEY, locale);
	} catch {
		// localStorage may throw; not fatal — locale will reset on reload.
	}
}

async function fetchLocalesCatalog(apiGatewayUrl: string, storeLabel: string): Promise<LocalesCatalog> {
	const base = apiGatewayUrl.replace(/\/$/, "");
	const url = `${base}/config/${storeLabel}/locales`;
	const response = await fetch(url, { credentials: "omit" });
	if (!response.ok) {
		throw new Error(`Failed to load locales catalog (HTTP ${response.status})`);
	}
	return response.json();
}

export function useLocaleViewModel(): UseLocaleViewModel {
	const { i18n } = useTranslation();
	// Seed synchronously from the already-loaded runtime config when it carries
	// the locales block (issue #140). The App gates rendering on config load, so
	// getCachedConfig() is populated by the time the switcher mounts — this lets
	// the switcher render on first paint with no async-catalog gate (no CLS, no
	// boot→default flash). The effect below is the fallback for configs that
	// don't embed locales yet.
	const [catalog, setCatalog] = useState<LocalesCatalog | null>(
		() => getCachedConfig()?.locales ?? null,
	);
	const [currentLocale, setCurrentLocale] = useState<string>(() => {
		const stored = readStoredLocale();
		return stored ?? i18n.language ?? FALLBACK_LOCALE;
	});

	useEffect(() => {
		let cancelled = false;
		(async () => {
			try {
				// Prefer the catalog embedded in the already-loaded runtime config.
				let resolved: LocalesCatalog | null = getCachedConfig()?.locales ?? null;
				if (!resolved) {
					// Fallback: runtime config didn't carry locales — fetch the
					// dedicated catalog endpoint.
					const runtime = await fetchRuntimeConfig();
					const apiGatewayUrl: string =
						(runtime.medusa?.backendUrl ?? "")
							.replace(/\/api\/v1\/tenants\/[^/]+.*$/, "")
							.replace(/\/store\/?$/, "") || "";
					const storeLabel: string = runtime.tenantPrefix ?? runtime.storeLabel ?? "intersum-ecommerce-example";
					resolved = await fetchLocalesCatalog(apiGatewayUrl, storeLabel);
				}
				if (cancelled || !resolved) return;
				setCatalog(resolved);

				// Apply the store default if the user has no prior choice and
				// the active locale is the global fallback.
				const stored = readStoredLocale();
				if (!stored && resolved.default && i18n.language !== resolved.default) {
					await i18n.changeLanguage(resolved.default);
					setCurrentLocale(resolved.default);
				}
			} catch (error) {
				// eslint-disable-next-line no-console
				console.warn("[useLocaleViewModel] failed to load locales catalog:", error);
			}
		})();
		return () => {
			cancelled = true;
		};
	}, [i18n]);

	const setLocale = useCallback(
		(locale: string) => {
			writeStoredLocale(locale);
			setCurrentLocale(locale);
			i18n.changeLanguage(locale).catch((error) => {
				// eslint-disable-next-line no-console
				console.warn(`[useLocaleViewModel] changeLanguage(${locale}) failed:`, error);
			});
		},
		[i18n],
	);

	const availableLocales = catalog?.supported ?? [];
	const switcherStyle: SwitcherStyle = catalog?.switcherStyle ?? "pill";
	const isReady = catalog !== null;

	return {
		currentLocale,
		availableLocales,
		switcherStyle,
		isReady,
		setLocale,
	};
}

export default useLocaleViewModel;
